/**
 * Review comment data model for code review functionality.
 * Comments can be added by users on file diffs or by agents via annotations.
 */

export type ReviewCommentAuthor = "user" | "agent";

/** Which side of the diff the comment is on */
export type CommentSide = "deletions" | "additions";

/** Selection state for adding a new comment on specific lines */
export type PendingCommentSelection = {
    filePath: string;
    startLine: number;
    endLine: number;
    side?: CommentSide;
};

/** Cached line data for displaying code context when environment is offline */
export type CachedCodeLine = {
    lineNumber: number;
    content: string;
    isHighlighted: boolean;
};

export type ReviewComment = {
    /** Unique identifier for the comment */
    id: string;
    /** Path to the file being commented on */
    filePath: string;
    /** Starting line number (1-based) */
    startLine: number;
    /** Ending line number (1-based), same as startLine for single-line comments */
    endLine: number;
    /** The comment content (supports markdown) */
    content: string;
    /** Who created the comment */
    author: ReviewCommentAuthor;
    /** When the comment was created */
    createdAt: number;
    /** When the comment was last updated */
    updatedAt: number;
    /** Which side of the diff the comment is on (deletions or additions) */
    side?: CommentSide;
    /** Hash of the code content at the time the comment was created (for detecting outdated comments) */
    contentHash?: string;
    /** Cached code lines for offline display */
    cachedCodeLines?: CachedCodeLine[];
    /** Original annotation ID from the agent (for agent-authored comments) */
    annotationId?: string;
    /** Whether the user gave negative feedback (thumbs down) - skipped when submitting */
    thumbsDown?: boolean;
};

export type ReviewState = {
    /** Comments indexed by environment ID */
    comments: ReviewComment[];
    /** Whether the review tab is visible */
    isReviewTabVisible: boolean;
};

/** Comments grouped by file path */
export type CommentsByFile = Partial<Record<string, ReviewComment[]>>;

export const createReviewComment = (
    filePath: string,
    startLine: number,
    endLine: number,
    content: string,
    author: ReviewCommentAuthor,
    annotationId?: string,
    side?: CommentSide,
    contentHash?: string,
): ReviewComment => ({
    id: crypto.randomUUID(),
    filePath,
    startLine,
    endLine,
    content,
    author,
    createdAt: Date.now(),
    updatedAt: Date.now(),
    side,
    annotationId,
    contentHash,
});
